import {
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode
} from 'react';
import { ArrowLeft, ArrowRight } from '../../../icons';
import { cx } from '../../../utils/cx';

export interface CarouselItem {
  id?: string | number;
  content: ReactNode;
}

export interface CarouselProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  items: CarouselItem[];
  itemWidthClassName?: string;
  /** Enables CSS scroll snapping on the carousel track */
  snap?: boolean;
}

export function Carousel({
  title,
  items,
  itemWidthClassName,
  snap = true,
  className,
  ...props
}: CarouselProps) {
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Tolerance of 1px for subpixel rendering differences
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Initial check
      checkScroll();

      // Use ResizeObserver to detect size changes
      const resizeObserver = new ResizeObserver(() => {
        checkScroll();
      });
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [items]);

  const handleScrollPrev = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScrollNext = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Scroll by roughly one visible chunk, or the width of the first item if possible
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section
      {...props}
      className={cx('flex flex-col gap-4', className)}
      aria-label={typeof title === 'string' ? title : undefined}
    >
      {title && (
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-xl font-bold text-[var(--gn-color-brand-primary-700)]">
              {title}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleScrollPrev}
              disabled={!canScrollLeft}
              aria-label="Scroll to previous items"
              className={cx(
                'inline-flex size-10 items-center justify-center rounded-xl border border-neutral-200 bg-white text-gray-700 shadow-sm transition hover:bg-neutral-50',
                !canScrollLeft && 'pointer-events-none opacity-0'
              )}
            >
              <ArrowLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={handleScrollNext}
              disabled={!canScrollRight}
              aria-label="Scroll to next items"
              className={cx(
                'inline-flex size-10 items-center justify-center rounded-xl border border-neutral-200 bg-white text-gray-700 shadow-sm transition hover:bg-neutral-50',
                !canScrollRight && 'cursor-not-allowed opacity-50'
              )}
            >
              <ArrowRight className="size-5" />
            </button>
          </div>
        </div>
      )}

      <ul
        ref={scrollContainerRef}
        role="list"
        className={cx(
          'flex gap-4 overflow-x-auto pb-3 scrollbar-hide',
          snap && 'snap-x snap-mandatory'
        )}
      >
        {items.map((item, itemIndex) => (
          <li
            key={item.id ?? itemIndex}
            className={cx(
              'flex-shrink-0',
              itemWidthClassName ?? 'w-[256px]',
              snap && 'snap-start'
            )}
          >
            {item.content}
          </li>
        ))}
      </ul>
    </section>
  );
}
